import { useState } from "react";
import {
  Group, Text, Badge, Button, Modal, Stack, Select, Divider, ScrollArea,
  Textarea, Loader, Anchor, Box, Alert,
} from "@mantine/core";
import {
  IconShieldLock, IconSend, IconDownload, IconRefresh, IconX, IconBan, IconCheck, IconLock, IconAlertTriangle,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  getCandidateConsent, createConsentRequest, resendConsentRequest, revokeConsentRequest,
  overrideConsent, downloadConsentCertificate,
} from "../api";
import { usePlan } from "../hooks/usePlan";
import { requestUpgrade } from "../Pricing/upgrade";
import InfoHint from "../components/InfoHint";

const UPGRADE_MSG = "Consent requests are a Pro feature. Upgrade to send and manage GDPR consent.";

const COLORS = {
  notRequested: "gray", requested: "yellow", granted: "teal",
  declined: "red", expired: "orange", withdrawn: "red",
};
const canon = (s) => (s === "pending" ? "notRequested" : s === "obtained" ? "granted" : s);
const humanize = (s) => (s || "").replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : "");

const VALIDITY = [
  { value: "months6", label: "6 mo" },
  { value: "months12", label: "12 mo" },
  { value: "months24", label: "24 mo" },
  { value: "indefinite", label: "∞" },
];

/**
 * Consent status badge + a management modal (send/resend/revoke requests, record offline
 * consent, suppress, download certificate, view the chain timeline). Self-contained; calls
 * onChanged() after mutations so the parent profile can refresh its candidate.
 */
export default function ConsentSection({ candidate, onChanged }) {
  const { t } = useTranslation("Veft");
  const { canUseConsent } = usePlan();
  const [opened, setOpened] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [validity, setValidity] = useState("months12");
  const [reason, setReason] = useState("");

  const status = canon(candidate.consentStatus);
  const overdue = candidate.consentOverdue;
  const badgeColor = overdue ? "orange" : COLORS[status] ?? "gray";
  const badgeLabel = t(`consent_status_${overdue ? "overdue" : status}`);

  const load = async () => {
    setLoading(true);
    try { setData(await getCandidateConsent(candidate.id)); }
    finally { setLoading(false); }
  };

  const open = () => { setOpened(true); load(); };

  const refresh = async () => { await load(); onChanged?.(); };
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await refresh(); }
    catch { /* api surfaces the error */ }
    finally { setBusy(false); }
  };
  // Proactively gate the creation actions; backend still enforces (402) as the real check.
  const gatedRun = (fn) => (canUseConsent ? run(fn) : requestUpgrade(UPGRADE_MSG));

  const pending = data?.requests?.find((r) => r.status === "pending");

  // No email → a consent request can't be sent (the backend rejects it). Block the
  // action proactively with a localized hint instead of letting the server 400 with
  // a raw English message. Only relevant when the feature is usable (else upgrade wins).
  const hasEmail = Boolean(
    candidate.email?.trim() || candidate.emails?.some((e) => e?.address?.trim())
  );
  const noEmailBlock = canUseConsent && !hasEmail;
  // Sending is locked until the tenant activates consent in Settings (guards against
  // firing the example template). Recording offline consent stays available — it sends nothing.
  const notActivatedBlock = canUseConsent && data?.activated === false;

  return (
    <>
      <Group gap="xs">
        <Text size="sm" fw={500}>{t("gdpr_consent")}:</Text>
        <Badge size="xs" color={badgeColor}>
          {badgeLabel}
          {status === "granted" && candidate.consentExpiry &&
            ` (${t("consent_until")} ${fmtDate(candidate.consentExpiry)})`}
          {status === "granted" && !candidate.consentExpiry && ` (${t("consent_indefinite")})`}
        </Badge>
        <InfoHint i18nKey="consent_states_help" multiline width={300} />
        {candidate.consentSuppressed && (
          <Badge size="xs" color="gray" variant="outline">{t("consent_status_suppressed")}</Badge>
        )}
        <Button size="compact-xs" variant="subtle" leftSection={<IconShieldLock size={13} />} onClick={open}>
          {t("consent_manage")}
        </Button>
      </Group>

      <Modal opened={opened} onClose={() => setOpened(false)} title={t("consent_manage")} size="lg">
        {loading || !data ? (
          <Group justify="center" p="lg"><Loader /></Group>
        ) : (
          <Stack gap="md">
            {/* Current status */}
            <Group gap="xs">
              <Badge color={data.overdue ? "orange" : COLORS[canon(data.status)] ?? "gray"}>
                {t(`consent_status_${data.overdue ? "overdue" : canon(data.status)}`)}
              </Badge>
              {data.expiry && <Text size="sm" c="dimmed">{t("consent_until")} {fmtDate(data.expiry)}</Text>}
              {data.suppressed && (
                <Badge color="gray" variant="outline">{t("consent_status_suppressed")}{data.suppressedReason ? `: ${data.suppressedReason}` : ""}</Badge>
              )}
            </Group>

            {/* Pending request → resend / revoke */}
            {pending ? (
              <Box>
                <Text size="sm">{t("consent_pending_sent_to", { email: pending.recipientEmail })}</Text>
                <Text size="xs" c="dimmed">{fmtDateTime(pending.sentAt)}</Text>
                <Group gap="xs" mt="xs">
                  <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} loading={busy}
                    onClick={() => run(() => resendConsentRequest(candidate.id, pending.id))}>
                    {t("consent_resend")}
                  </Button>
                  <Button size="xs" variant="light" color="red" leftSection={<IconX size={14} />} loading={busy}
                    onClick={() => run(() => revokeConsentRequest(candidate.id, pending.id))}>
                    {t("consent_revoke")}
                  </Button>
                </Group>
              </Box>
            ) : (
              /* No pending → send a new request */
              <Stack gap="xs">
                {notActivatedBlock && (
                  <Alert color="yellow" variant="light" p="xs" icon={<IconLock size={16} />}>
                    <Text size="xs">{t("consent_not_activated_warning")}</Text>
                  </Alert>
                )}
                {noEmailBlock && (
                  <Alert color="yellow" variant="light" p="xs" icon={<IconAlertTriangle size={16} />}>
                    <Text size="xs">{t("consent_no_email_warning")}</Text>
                  </Alert>
                )}
                <Group gap="xs" align="flex-end">
                  <Select label={t("consent_validity")} data={VALIDITY} value={validity}
                    onChange={setValidity} size="xs" style={{ width: 120 }} allowDeselect={false} />
                  <Button size="xs" leftSection={canUseConsent ? <IconSend size={14} /> : <IconLock size={14} />} loading={busy}
                    disabled={noEmailBlock || notActivatedBlock}
                    onClick={() => gatedRun(() => createConsentRequest(candidate.id, { validityPeriod: validity }))}>
                    {t("consent_send_request")}
                  </Button>
                </Group>
              </Stack>
            )}

            <Divider />

            {/* Overrides */}
            <Group gap="xs" align="flex-end" wrap="wrap">
              <Button size="xs" variant="default" leftSection={canUseConsent ? <IconCheck size={14} /> : <IconLock size={14} />} loading={busy}
                onClick={() => gatedRun(() => overrideConsent(candidate.id, { action: "grantOffline", validityPeriod: validity }))}>
                {t("consent_record_offline")}
              </Button>
              {data.suppressed ? (
                <Button size="xs" variant="default" loading={busy}
                  onClick={() => run(() => overrideConsent(candidate.id, { action: "unsuppress" }))}>
                  {t("consent_clear_suppression")}
                </Button>
              ) : (
                <Group gap="xs" align="flex-end">
                  <Textarea label={t("consent_suppress_reason")} value={reason} onChange={(e) => setReason(e.currentTarget.value)}
                    autosize minRows={1} size="xs" style={{ width: 200 }} />
                  <Button size="xs" variant="default" color="gray" leftSection={<IconBan size={14} />} loading={busy}
                    onClick={() => run(() => overrideConsent(candidate.id, { action: "suppress", reason }))}>
                    {t("consent_mark_not_required")}
                  </Button>
                </Group>
              )}
            </Group>

            {/* Decided requests → certificate */}
            {data.requests?.some((r) => r.status === "approved" || r.status === "declined") && (
              <>
                <Divider label={t("consent_requests")} labelPosition="left" />
                <Stack gap={6}>
                  {data.requests.filter((r) => r.status === "approved" || r.status === "declined").map((r) => (
                    <Group key={r.id} justify="space-between" gap="xs">
                      <Group gap="xs">
                        <Badge size="xs" color={r.status === "approved" ? "teal" : "red"}>{r.status}</Badge>
                        <Text size="xs" c="dimmed">{fmtDateTime(r.respondedAt)}</Text>
                      </Group>
                      <Anchor size="xs" onClick={() => downloadConsentCertificate(candidate.id, r.id)}>
                        <Group gap={4}><IconDownload size={13} />{t("consent_download_certificate")}</Group>
                      </Anchor>
                    </Group>
                  ))}
                </Stack>
              </>
            )}

            {/* Chain timeline */}
            <Divider label={t("consent_timeline")} labelPosition="left" />
            {data.events?.length ? (
              <ScrollArea.Autosize mah={220}>
                <Stack gap={4}>
                  {[...data.events].reverse().map((e) => (
                    <Group key={e.sequence} gap="xs" wrap="nowrap" align="baseline">
                      <Text size="xs" c="dimmed" style={{ width: 130, flexShrink: 0 }}>{fmtDateTime(e.timestamp)}</Text>
                      <Text size="xs" fw={500}>{t(`consent_event_${e.eventType}`, { defaultValue: humanize(e.eventType) })}</Text>
                      <Text size="xs" c="dimmed">
                        {t(`consent_actor_${e.actorType}`, { defaultValue: e.actorType })}{e.clientIp ? ` · ${e.clientIp}` : ""}{e.detail ? ` · ${e.detail}` : ""}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            ) : (
              <Text size="xs" c="dimmed">{t("consent_no_history")}</Text>
            )}
          </Stack>
        )}
      </Modal>
    </>
  );
}
